import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Upload, Download, CheckCircle, FileText, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsCartes({ navigate }) {
  const [cartes, setCartes] = useState([]);
  const [uploading, setUploading] = useState({});

  const load = () => fetch(`${API}/api/site/cartes-pdf`).then(r => r.json()).then(setCartes).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleUpload = async (carteId, file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return;
    setUploading(p => ({ ...p, [carteId]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      await fetch(`${API}/api/site/cartes-pdf/${carteId}/upload`, { method: 'POST', body: form });
      await load();
    } catch (e) { console.error(e); }
    setUploading(p => ({ ...p, [carteId]: false }));
  };

  const handleDelete = async (carteId) => {
    await fetch(`${API}/api/site/cartes-pdf/${carteId}`, { method: 'DELETE' });
    load();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    return `${Math.round(bytes / 1024)} Ko`;
  };

  return (
    <div data-testid="cms-cartes">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm" data-testid="cartes-back-btn">
          <ArrowLeft size={16} /> Retour
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gestion des Cartes PDF</h1>
          <p className="text-sm text-gray-500">Uploadez vos cartes pour le telechargement client</p>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <FileText size={20} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-sm text-blue-900">Comment ca fonctionne ?</p>
          <p className="text-sm text-blue-700">
            Uploadez vos cartes au format PDF. Les clients pourront les telecharger depuis la page "La Carte" du site.
            Vous pouvez creer vos cartes avec Word, Canva, ou tout autre logiciel puis les exporter en PDF.
          </p>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cartes.map(carte => (
          <CarteSlot
            key={carte.id}
            carte={carte}
            uploading={uploading[carte.id]}
            onUpload={(file) => handleUpload(carte.id, file)}
            onDelete={() => handleDelete(carte.id)}
            formatSize={formatSize}
          />
        ))}
      </div>
    </div>
  );
}

function CarteSlot({ carte, uploading, onUpload, onDelete, formatSize }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`carte-slot-${carte.id}`}>
      {/* File info */}
      {carte.active && carte.filename && (
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
          <FileText size={16} className="text-gray-500" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-gray-900 truncate">{carte.label}</p>
            <p className="text-xs text-gray-400">
              {formatSize(carte.size)}
              {carte.updated_at && ` · Modifie le ${new Date(carte.updated_at).toLocaleDateString('fr-FR')}`}
            </p>
          </div>
        </div>
      )}

      {!carte.active && (
        <div className="mb-4 pb-3 border-b border-gray-100">
          <p className="font-medium text-sm text-gray-900">{carte.label}</p>
          <p className="text-xs text-gray-400">Aucun PDF uploade</p>
        </div>
      )}

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? (
          <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full mx-auto" />
        ) : (
          <>
            <Upload size={24} className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Glissez-deposez votre PDF ici</p>
            <p className="text-xs text-gray-400 mt-1">ou</p>
          </>
        )}
      </div>

      <button
        onClick={() => fileRef.current?.click()}
        className="w-full mt-3 bg-[#1a2744] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#243559] transition-colors"
        data-testid={`upload-${carte.id}-btn`}
      >
        Parcourir les fichiers
      </button>

      <input ref={fileRef} type="file" accept=".pdf" className="hidden"
        onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />

      {/* Status */}
      <div className={`mt-3 flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
        carte.active ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-200'
      }`}>
        <div className="flex items-center gap-2">
          {carte.active ? <CheckCircle size={16} className="text-teal-600" /> : <FileText size={16} className="text-gray-400" />}
          <span className={carte.active ? 'text-teal-700 font-medium' : 'text-gray-500'}>
            {carte.active ? 'PDF actif' : 'Aucun PDF'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {carte.active && carte.url && (
            <a href={`${API}${carte.url}`} target="_blank" rel="noreferrer" className="p-1 hover:bg-teal-100 rounded" title="Telecharger">
              <Download size={16} className="text-teal-600" />
            </a>
          )}
          {carte.active && (
            <button onClick={onDelete} className="p-1 hover:bg-red-100 rounded" title="Supprimer" data-testid={`delete-pdf-${carte.id}`}>
              <Trash2 size={16} className="text-red-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
